import React, { useState, useEffect, useRef } from "react";
import { clearSession } from "../shared/_helpers/session";
import { useNavigate, Link } from "react-router-dom";
import socket, { joinChat } from "../shared/_helpers/socket";
import { checkBlockedStatus, getClientsChatList, getParticularClientChat } from "../shared/_services/common.services";
import { setUnseenCount } from "../redux/chatReducer";
import { useDispatch } from "react-redux";
import chatPlaceholder from "../assets/images/chatPlaceholder.png";
import moment from "moment";


function getClientInitials(client) {
    const firstInitial = client?.firstName ? client.firstName.trim().charAt(0) : "";
    const lastInitial = client?.lastName ? client.lastName.trim().charAt(0) : "";
    return `${firstInitial}${lastInitial}`;
}

function buildProfileImageUrl(profileImage) {
    return `${process.env.REACT_APP_BACKEND}/uploads/${profileImage}`;
}

function getLastMessagePreview(lastContent) {
    if (typeof lastContent === "string") return lastContent;
    return lastContent?.message || "";
}

function normalizeMessageContent(rawContent) {
    if (typeof rawContent === "string") {
        return { messageText: rawContent, messageType: "text", messageUrl: "" };
    }

    return {
        messageText: rawContent?.message || "",
        messageType: rawContent?.type || "text",
        messageUrl: rawContent?.url || "",
    };
}

function getMessageClass(msg, sender) {
    if (msg?.sender?.userType) {
        return msg.sender.userType === "client" ? "rcvd-mesg" : "sent-mesg";
    }
    return msg.sender === sender ? "sent-mesg" : "rcvd-mesg";
}

function renderMessageContent(rawContent) {
    const { messageText, messageType, messageUrl } = normalizeMessageContent(rawContent);
    if (messageType === "file" && messageUrl) {
        return (
            <a href={messageUrl} target="_blank" rel="noopener noreferrer">
                {messageText}
            </a>
        );
    }
    return messageText;
}

function formatMessageClockTime(createdAt) {
    return createdAt ? moment(createdAt).format("hh:mm A") : "Just now";
}

function ClientAvatar({ client, alt = "Profile" }) {
    if (client?.profile_image) {
        return <img src={buildProfileImageUrl(client.profile_image)} alt={alt} />;
    }

    return <h3>{getClientInitials(client)}</h3>;
}

const AdvisorChatScreen = () => {
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState(false);
    const [currentMessage, setCurrentMessage] = useState("");
    const [chatData, setChatData] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const sender = JSON.parse(localStorage.getItem("currentUser"))?.user._id;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const lastMessageRef = useRef(null);
    const [msgcoutn, setMsgcount] = useState(false);


    const checkBlocked = async () => {
        const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
        try {
            // Call the API to check the user's blocked status
            const response = await checkBlockedStatus(userId);
            if (response?.response?.status === 401) {
                clearSession();
                navigate("/login");
            }
            else {
                const { isBlocked, isDeleted } = response.data;

                if (isBlocked || isDeleted) {
                    // Clear the currentUser from localStorage
                    clearSession();

                    // Navigate to the login page
                    navigate("/login");
                }
            }
        } catch (error) {
            console.error("Error checking blocked status:", error);
            // Optionally handle errors, e.g., show an error message
        }
    }

    useEffect(() => {
        const fetchChatData = async () => {
            const response = await getClientsChatList();
            const storeChatData = response?.data?.data || [];
            const filteredChatData = storeChatData.filter(chat => chat.lastMessage?.id?.content);
            const newChatData = filteredChatData.sort((a, b) => {
                const dateA = new Date(a.lastMessage?.id?.updatedAt || a.updatedAt);
                const dateB = new Date(b.lastMessage?.id?.updatedAt || b.updatedAt);
                return dateB - dateA;
            });
            setChatData(newChatData);
            // Calculate unseen count directly from the response data
            // const unseenCount = newChatData.filter(chat => !chat.lastMessage.seen).length;

            const currentUserId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;

            const unseenCount = newChatData.filter(chat =>
                !chat.lastMessage?.seen && chat.lastMessage?.id?.sender !== currentUserId
            ).length;
             setMsgcount(false);
            // Dispatch the count to Redux
            dispatch(setUnseenCount(unseenCount));
        };

        fetchChatData();
        checkBlocked();
        socket.on("chat list updated", fetchChatData); // Refresh instantly when triggered

        return () => {
            socket.off("chat list updated", fetchChatData);
        };
    }, [dispatch,msgcoutn]); // eslint-disable-line


    useEffect(() => {
        if (!selectedChat) return;
        joinChat(selectedChat._id);

        socket.on('message received', (newMessage) => {
            const incoming = Array.isArray(newMessage) ? newMessage : [newMessage];
            setMessages((prevMessages) => [...prevMessages, ...incoming]);
        });


        socket.on('typing', (status) => {
            setTyping(status);


            setTimeout(() => {
                setTyping('');
            }, 3000);
        });

        return () => {
            socket.off("typing");
            socket.off("message received");
        };
    }, [selectedChat?._id]); // eslint-disable-line


    const handleSendMessage = () => {
        if (currentMessage.trim() === "") return;

        const payload = {
            chatId: selectedChat._id,
            sender,
            content: {
                message: currentMessage,
                type: "text",
                url: ""
            }
        };

        // Backend now expects payload as an array of messages
        socket.emit("new message", [payload]);
        setCurrentMessage("");
    };

    const handleTyping = () => {
        if (!selectedChat) return;
        socket.emit("typing", { chatId: selectedChat._id });
    };

    const handleChatClick = async (chat) => {
        setMsgcount(true);
        const updatedChatData = chatData.map((item) => {
            if (item._id === chat._id) {
                return {
                    ...item,
                    lastMessage: {
                        ...item.lastMessage,
                        seen: true,
                    },
                };
            }
            return item;
        });

        setChatData(updatedChatData);
        setSelectedChat(chat);
        const response = await getParticularClientChat(chat._id);
        setMessages(response?.data?.data || []);
    };


    const formatTimestamp = (timestamp) => {
        const now = new Date();
        const messageDate = new Date(timestamp);
        const timeDiff = now - messageDate;

        const oneMinute = 60 * 1000; // 1 minute in milliseconds
        const oneHour = 60 * oneMinute; // 1 hour in milliseconds
        const oneDay = 24 * oneHour; // 1 day in milliseconds

        const minutesDiff = Math.floor(timeDiff / oneMinute);
        const hoursDiff = Math.floor(timeDiff / oneHour);
        const daysDiff = Math.floor(timeDiff / oneDay);

        if (timeDiff < oneMinute) {
            return "Just now"; // Less than 1 minute ago
        } else if (timeDiff < oneHour) {
            return `${minutesDiff} minute${minutesDiff > 1 ? "s" : ""} ago`; // 1-59 minutes ago
        } else if (timeDiff < oneDay) {
            return `${hoursDiff} hour${hoursDiff > 1 ? "s" : ""} ago`; // 1-23 hours ago
        } else if (daysDiff === 1) {
            return "Yesterday"; // 1 day ago
        } else {
            return formatDate(messageDate);
        }
    };

    const formatDate = (dateString) => {
        const date = moment(dateString);
        if (date.isSame(moment(), "day")) {
            return "Today";
        } else if (date.isSame(moment().subtract(1, "day"), "day")) {
            return "Yesterday";
        } else {
            return date.format("DD MMMM YYYY");
        }
    };

    // Track the last displayed date
    let lastDisplayedDate = null;

    // Scroll to the last message whenever the messages array changes
    useEffect(() => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    return (
        <>
            <div className="page-header">
                <h2>Messages</h2>
            </div>
            <div className="p-4">
                <div className="advisor-chat-screen">
                    {/* Left Side Panel */}
                    <div className="recent-chats-sec mini-scroll-bar">
                        <div className="chat-head">
                            <h4 className="mb-0">Messages</h4>
                        </div>
                        <div className="recent-chats-body pt-2">

                            {chatData.map((chat) => {
                                const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
                                const isAdvisorMessage = chat?.lastMessage?.id?.sender === userId;
                                const isSeen = isAdvisorMessage || chat.lastMessage.seen;
                                const clientName = `${chat?.client?.firstName} ${chat?.client?.lastName}`;
                                const previewText = getLastMessagePreview(chat?.lastMessage?.id?.content);

                                return (
                                    <div
                                        key={chat._id}
                                        className={`recent-chat-item ${isSeen ? "" : "chat-unread"} ${selectedChat?._id === chat._id ? "active-chat" : ""}`}
                                        onClick={() => handleChatClick(chat)}
                                    >
                                        <div className="profile-pic">
                                            <ClientAvatar client={chat?.client} />
                                        </div>
                                        <div className="place-content-center w-100">
                                            <h4 className="username mb-0">
                                                {clientName}
                                            </h4>
                                            <div className="latest-msg">
                                                <p>
                                                    {previewText}
                                                </p>
                                                <span style={{ paddingLeft: "30px" }}>
                                                    {formatTimestamp(chat?.lastMessage?.id?.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                        </div>
                    </div>

                    {/* Chat Screen */}
                    <div className="current-open-chat">
                        {selectedChat ? (
                            <>
                                <div className="current-open-chat-head">
                                    <Link
                                        to={`/dashboard/${selectedChat?.client?._id}?client=true`}
                                        className="d-flex align-items-center"
                                    >
                                        <div className="profile-pic">
                                            <ClientAvatar client={selectedChat?.client} />
                                        </div>
                                        <h4 className="username mb-0">
                                            {selectedChat?.client?.firstName} {selectedChat?.client?.lastName}
                                        </h4>
                                    </Link>
                                </div>
                                <div className="current-open-chat-body">
                                    {messages?.map((msg, index) => {
                                        const messageDate = formatDate(msg.createdAt);
                                        const showDate = messageDate !== lastDisplayedDate;
                                        lastDisplayedDate = messageDate;
                                        const messageClass = getMessageClass(msg, sender);

                                        return (
                                            <React.Fragment key={msg?._id || msg?.createdAt || index}>
                                                {showDate && <p className="msg-date">{messageDate}</p>}
                                                <div className={messageClass}>
                                                    <div className="profile-pic">
                                                        <ClientAvatar client={selectedChat?.client} />
                                                    </div>
                                                    <p>
                                                        {renderMessageContent(msg?.content)}
                                                        <div style={{ color: "#737373" }}>
                                                            {formatMessageClockTime(msg.createdAt)}
                                                        </div>
                                                    </p>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                    <div ref={lastMessageRef} />
                                </div>
                                <div className="advisor-chat-input">
                                    <input
                                        placeholder={typing ? "Tying..." : "Send Message"}
                                        type="text"
                                        value={currentMessage}
                                        onChange={(e) => setCurrentMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            handleTyping();
                                            if (e.key === "Enter") {
                                                handleSendMessage();
                                            }
                                        }} />
                                    <button className="send-btn" onClick={handleSendMessage}>
                                        <svg
                                            width="16"
                                            height="14"
                                            viewBox="0 0 16 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M1.5918 1.71196L2.38095 6.24955H7.25013C7.66434 6.24955 8.00013 6.58533 8.00013 6.99955C8.00013 7.41376 7.66434 7.74955 7.25013 7.74955H2.38095L1.5918 12.2871L13.9295 6.99955L1.5918 1.71196ZM0.988869 6.99955L0.0637969 1.68038C-0.0109877 1.25037 0.128154 0.810863 0.436783 0.502233C0.824568 0.114453 1.40938 0.0018234 1.91345 0.217853L15.3158 5.96171C15.7309 6.13964 16.0001 6.54786 16.0001 6.99955C16.0001 7.45123 15.7309 7.85946 15.3158 8.03739L1.91345 13.7812C1.40938 13.9973 0.824568 13.8846 0.436783 13.4969C0.128154 13.1882 -0.0109879 12.7487 0.0637969 12.3187L0.988869 6.99955Z"
                                                fill="#8E8E93"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="user-chat-placeholder p-3">
                                <h2>Welcome to astra chat!</h2>
                                <img src={chatPlaceholder} alt="chat"/>
                                <p>Select a chat to start messaging</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdvisorChatScreen;
